import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, List, Button, Empty, Spin, message, Typography, Popconfirm } from 'antd';
import { 
  BellOutlined, 
  CheckOutlined, 
  DeleteOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import socketService from '../../services/socketService';
import notificationService, { Notification } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import './NotificationBell.css';

const { Text } = Typography;

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Charger les notifications au montage
  useEffect(() => {
    console.log('🔔 NotificationBell mounted, currentUser:', currentUser);
    
    if (currentUser) {
      console.log('✅ CurrentUser exists, ID:', currentUser.id);
      loadNotifications();
      loadUnreadCount();
      
      // Connecter Socket.IO
      console.log('🔌 Tentative de connexion Socket.IO avec user:', currentUser.id);
      socketService.connect(currentUser.id);
      console.log('✅ Connexion Socket.IO lancée');

      // Écouter les nouvelles notifications
      const handleNewNotification = (notification: Notification) => {
        console.log('🔔 Nouvelle notification:', notification);
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Jouer le son
        try {
          const audio = new Audio('/notification.wav');
          audio.volume = 0.5;
          audio.play().catch(e => console.log('Autoplay empêché par le navigateur:', e));
        } catch (error) {
          console.log('Erreur lecture audio:', error);
        }
        
        // Afficher un toast
        message.info({
          content: notification.title,
          duration: 4,
          icon: getNotificationIcon(notification.priority)
        });
      };

      socketService.on('new_notification', handleNewNotification);

      // Cleanup
      return () => {
        socketService.off('new_notification', handleNewNotification);
      };
    }
  }, [currentUser]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications({ limit: 20 });
      
      // Debug: afficher la première notification
      if (response.data && response.data.length > 0) {
        console.log('📊 Première notification reçue du backend:', response.data[0]);
        console.log('   - created_at:', response.data[0].created_at);
        console.log('   - Type:', typeof response.data[0].created_at);
      }
      
      setNotifications(response.data);
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Erreur comptage notifications:', error);
    }
  };

  const handleMarkAsRead = async (notification: Notification, e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    if (notification.is_read) return;

    try {
      await notificationService.markAsRead(notification.id);
      socketService.markAsRead(notification.id);
      
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      message.error('Erreur lors du marquage');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      if (currentUser) {
        socketService.markAllAsRead(currentUser.id);
      }
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      message.success('Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      message.error('Erreur lors du marquage');
    }
  };

  const handleDelete = async (notificationId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      message.success('Notification supprimée');
    } catch (error) {
      message.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAll = async () => {
    try {
      const result = await notificationService.deleteAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
      message.success(`${result.count} notification(s) supprimée(s)`);
    } catch (error) {
      message.error('Erreur lors de la suppression des notifications');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Marquer comme lue
    if (!notification.is_read) {
      handleMarkAsRead(notification);
    }

    // Naviguer vers l'URL d'action si elle existe
    let targetUrl = notification.action_url;
    
    // Redirection automatique pour les notifications de contrats vers la page des contrats programmés
    if (
      notification.type === 'contract_activated' || 
      notification.title?.toLowerCase().includes('contrat') ||
      notification.message?.toLowerCase().includes('contrat')
    ) {
      targetUrl = '/contrats-programmes';
    } else if (targetUrl) {
      if (targetUrl.startsWith('/contracts') || targetUrl.startsWith('/contrats')) {
        targetUrl = '/contrats-programmes';
      }
      if (targetUrl.startsWith('/orders')) {
        targetUrl = targetUrl.replace('/orders', '/commandes');
      }
    }

    if (targetUrl) {
      setDropdownVisible(false);
      navigate(targetUrl);
    }
  };

  const getNotificationIcon = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'high':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'medium':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <BellOutlined style={{ color: '#52c41a' }} />;
    }
  };

  const getNotificationColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return '#fff1f0';
      case 'high':
        return '#fffbe6';
      case 'medium':
        return '#e6f7ff';
      default:
        return '#f6ffed';
    }
  };

  const formatDate = (dateInput?: string | Date | null) => {
    if (!dateInput) return 'À l\'instant';
    
    const date = new Date(dateInput);
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      console.warn('Date invalide:', dateInput);
      return 'À l\'instant';
    }
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1 || isNaN(diff) || diff < 0) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const dropdownMenu = (
    <div className="notification-dropdown">
      <div className="notification-header">
        <Text strong>Notifications</Text>
        <div style={{ display: 'flex', gap: '4px' }}>
          {unreadCount > 0 && (
            <Button 
              type="link" 
              size="small" 
              onClick={handleMarkAllAsRead}
              icon={<CheckOutlined />}
            >
              Tout marquer comme lu
            </Button>
          )}
          {notifications.length > 0 && (
            <Popconfirm
              title="Supprimer toutes les notifications ?"
              description="Cette action est irréversible."
              onConfirm={handleDeleteAll}
              okText="Oui, supprimer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<ClearOutlined />}
              >
                Vider tout
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>

      <div className="notification-list">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty 
            description="Aucune notification" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '20px' }}
          />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(notification) => (
              <List.Item
                key={notification.id}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                style={{ 
                  backgroundColor: !notification.is_read ? getNotificationColor(notification.priority) : 'white',
                  cursor: notification.action_url ? 'pointer' : 'default'
                }}
                onClick={() => handleNotificationClick(notification)}
                actions={[
                  !notification.is_read && (
                    <Button
                      type="text"
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={(e) => handleMarkAsRead(notification, e)}
                      title="Marquer comme lu"
                    />
                  ),
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => handleDelete(notification.id, e)}
                    title="Supprimer"
                  />
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={getNotificationIcon(notification.priority)}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong={!notification.is_read}>{notification.title}</Text>
                      {!notification.is_read && (
                        <Badge status="processing" />
                      )}
                    </div>
                  }
                  description={
                    <div>
                      <Text type="secondary">{notification.message}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {formatDate(notification.created_at || notification.createdAt)}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>

      {notifications.length > 0 && (
        <div className="notification-footer">
          <Button type="link" block onClick={() => {
            setDropdownVisible(false);
            navigate('/notifications');
          }}>
            Voir toutes les notifications
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      overlay={dropdownMenu}
      trigger={['click']}
      placement="bottomRight"
      open={dropdownVisible}
      onOpenChange={setDropdownVisible}
      overlayClassName="notification-dropdown-overlay"
    >
      <Badge count={unreadCount} offset={[-5, 5]} size="small">
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: '20px' }} />}
          style={{ marginRight: '10px' }}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell;
